import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { UserActionTypeAreaMap, UserActionTypeMap } from '@common/constants';
import { BooleanOperator } from '@common/enums';
import { AuditODataItem } from '@common/models';
import { ODataPagedResult, ODataService } from '@common/odata';
import { UserActionTypeAreaPipe, UserActionTypePipe } from '@common/pipes';
import { SharedOdataService } from '@common/services';
import { AuditLogTag, AuditLogTagsMap, getUniqueListByProp } from '@common/services/smart-search';
import { FilterOptions } from '@common/types';
import {
  containsWrapper,
  filterByWords,
  getFilterByContains,
  getFilterByDateRange,
  getFilterByEqFromEnum
} from '@common/utils/functions/search';
import { SmartSearchModel, SmartSearchModelField } from 'mbs-ui-kit';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class AuditService extends SharedOdataService<AuditODataItem> {
  private readonly http = inject(HttpClient);

  readonly #event = AuditLogTagsMap.get(AuditLogTag.Event);
  readonly #action = AuditLogTagsMap.get(AuditLogTag.Action);
  readonly #userName = AuditLogTagsMap.get(AuditLogTag.UserName);
  readonly #dateFrom = AuditLogTagsMap.get(AuditLogTag.DateFrom);
  readonly #dateTo = AuditLogTagsMap.get(AuditLogTag.DateTo);

  private userActionTypePipe = new UserActionTypePipe();
  private userActionTypeAreaPipe = new UserActionTypeAreaPipe();

  constructor() {
    super('Audit');
    this.orderBy = 'Date desc';
  }

  getAuditLog(): Observable<ODataPagedResult<AuditODataItem>> {
    const odataService = this.odataFactory.CreateService<AuditODataItem>(`/Audit`);

    return this.fetchData<AuditODataItem>(odataService);
  }

  /*
   * Get top 20 events in 'Event' section that match `containsValue`
   * @param {string} containsValue search string
   */
  public getAuditLogInEventSection(containsValue: string): Observable<string[]> {
    return this.getAuditLog().pipe(
      map(({ count, data }) => data),
      map((items) => {
        const filtered = items.filter((item) => item.UserActionTypeArea.toLowerCase().includes(containsValue));

        return getUniqueListByProp(filtered, this.#event.prop)
          .slice(0, 10)
          .map((item) => this.userActionTypeAreaPipe.transform(item.UserActionTypeArea));
      })
    );
  }

  /*
   * Get top 20 actions in 'Action' section that match `containsValue`
   * @param {string} containsValue search string
   */
  public getAuditLogInActionSection(containsValue: string): Observable<string[]> {
    return this.getAuditLog().pipe(
      map(({ count, data }) => data),
      map((items) => {
        const filtered = items.filter((item) => item.UserActionType.toLowerCase().includes(containsValue));

        return getUniqueListByProp(filtered, this.#action.prop)
          .slice(0, 10)
          .map((item) => this.userActionTypePipe.transform(item.UserActionType));
      })
    );
  }

  /*
   * Get top 10 user names that match `containsValue`
   * @param {string} containsValue search string
   */
  public getAuditLogInUserNameSection(containsValue: string): Observable<string[]> {
    return this.getAuditLog().pipe(
      map(({ count, data }) => data),
      map((items) => {
        const filtered = items.filter((item) => item.UserName.toLowerCase().includes(containsValue));

        return getUniqueListByProp(filtered, this.#userName.prop)
          .slice(0, 10)
          .map((item) => item.UserName);
      })
    );
  }

  exportToCSV(): Observable<Blob> {
    const odataService: ODataService<Blob> = this.odataFactory.CreateService<Blob>(`/Audit/ExportToCsv?${this.getQueryParams(this.odata)}`);

    return this.http.get(odataService.Query().GetUrl(), { responseType: 'blob' });
  }

  updateFilter(searchObj: SmartSearchModel): void {
    const filter: string[] = [];

    if (searchObj[this.#userName.tag]) {
      const options: FilterOptions = {
        model: searchObj[this.#userName.tag] as SmartSearchModelField[],
        prop: this.#userName.prop
      };

      filter.push(getFilterByContains(options));
    }

    if (searchObj[this.#event.tag]) {
      const options: FilterOptions = {
        model: searchObj[this.#event.tag] as SmartSearchModelField[],
        prop: this.#event.prop,
        enumItems: UserActionTypeAreaMap
      };

      filter.push(getFilterByEqFromEnum(options));
    }

    if (searchObj[this.#action.tag]) {
      const options: FilterOptions = {
        model: searchObj[this.#action.tag] as SmartSearchModelField[],
        prop: this.#action.prop,
        enumItems: UserActionTypeMap
      };

      filter.push(getFilterByEqFromEnum(options));
    }

    if (searchObj[this.#dateFrom.tag]) {
      const options: FilterOptions = {
        model: searchObj[this.#dateFrom.tag] as SmartSearchModelField[],
        prop: this.#dateFrom.prop
      };
      const value = getFilterByDateRange(options, 'from');

      value && filter.push(value);
    }

    if (searchObj[this.#dateTo.tag]) {
      const options: FilterOptions = {
        model: searchObj[this.#dateTo.tag] as SmartSearchModelField[],
        prop: this.#dateTo.prop
      };
      const value = getFilterByDateRange(options, 'to');

      value && filter.push(value);
    }

    if (searchObj.words?.filter(Boolean)) {
      const term = filterByWords(searchObj);

      filter.push(containsWrapper(this.#userName.prop, term));
    }

    this.filter = filter.length > 0 ? filter.join(` ${BooleanOperator.And} `) : '';
  }
}
